/**
 * The month view's read model.
 *
 * ONE STATUS VOCABULARY, AND IT IS THE SYSTEM'S. The shipped grid carries its
 * own `TONE_ACCENT` ramp — a fourth vocabulary running beside the stage ramp,
 * the pillar tokens and the status tokens — and it paints machine work in
 * `--co-accent`, which DESIGN.md reserves for your choice or your attention.
 * The design system documents exactly two status colours and what each is for:
 *
 *   "Something is running right now  -> --co-live      -> As: Dot"
 *   "Something is waiting on a person -> --co-attention -> Dot or label"
 *
 * So that is all the colour this page spends on state. Ready, queued and missed
 * are carried by their words, because they ask nothing of anybody and the 90/10
 * rule is explicit: "Colour is an event, not a surface. If a screen has colour
 * in five places, four of them are wrong."
 */
import type { CalendarPlanSlot } from "@/types/contentCalendar.type";
import type { BriefRailInfo } from "@/types/productionBrief.type";
import { slotTone, type SlotTone } from "../slotStage";
import {
  CONTENT_STAGES,
  STAGE_LABEL,
  isMissed,
  stageDetail,
  stageOf,
  type ContentStage,
} from "./contentStage";

/** What a post's mark is, if it has one at all. */
export type Mark = "running" | "waiting" | "none";

export function markOf(tone: SlotTone): Mark {
  if (tone === "machine") return "running";
  // A missed post is a broken promise sitting on a past day; it is waiting on a
  // person exactly as much as an undecided one, and it was rendering as the
  // quietest thing on the screen.
  if (tone === "human" || tone === "missed") return "waiting";
  return "none";
}

/**
 * The one word a card shows. Never a colour on its own.
 *
 * THE STAGE, not the slot's raw status. The two disagree on exactly the posts
 * that matter most: a post already live on Instagram still has a slot status of
 * CLIENT_APPROVED, so the card said "Client approved" while the list said
 * "Posted". The stage reads all three tables and is the vocabulary Emad set, so
 * it is the one both shapes speak.
 *
 * `missed` still wins, because it is not a stage — it is a post whose day
 * passed, and that is the thing to say about it.
 */
export function wordFor(tone: SlotTone, stage: ContentStage): string {
  if (tone === "missed") return "Missed";
  return STAGE_LABEL[stage];
}

export interface SlotRead {
  slot: CalendarPlanSlot;
  tone: SlotTone;
  mark: Mark;
  word: string;
  /** The stage spans slot, brief and publication, so the read carries all of it. */
  brief: BriefRailInfo | null;
  stage: ContentStage;
  detail: string | null;
}

export function readSlot(
  slot: CalendarPlanSlot,
  briefBySlot: Map<string, BriefRailInfo> | undefined,
  now: number,
): SlotRead {
  const tone = slotTone(
    slot,
    briefBySlot ? { known: true, brief: briefBySlot.get(slot.id) ?? null } : { known: false },
    now,
  );
  const brief = briefBySlot?.get(slot.id) ?? null;
  const stage = stageOf(slot, brief);
  return {
    slot,
    tone,
    mark: markOf(tone),
    word: wordFor(tone, stage),
    brief,
    stage,
    detail: stageDetail(slot, brief),
  };
}

/** The month in one sentence: what it holds, and what it wants from you. */
export interface MonthRead {
  total: number;
  waiting: number;
  running: number;
  withClient: number;
  missed: number;
}

export function readMonth(reads: SlotRead[]): MonthRead {
  return {
    total: reads.length,
    waiting: reads.filter((r) => r.mark === "waiting").length,
    running: reads.filter((r) => r.mark === "running").length,
    withClient: reads.filter((r) => r.slot.status === "IN_CLIENT_REVIEW").length,
    missed: reads.filter((r) => r.tone === "missed").length,
  };
}

/**
 * The axes the bar filters by: the six STAGES, plus missed.
 *
 * The old set mixed two different questions — "Needs you" and "Running" are
 * about whose turn it is, "Done" and "Not started" about how far it has got —
 * so it could never be read down as a sequence. These are one question, asked
 * once, in the order the work actually happens.
 *
 * They still have to PARTITION the month. Missed is not a stage, so it would
 * otherwise sit inside "Not started" and the counts would stop summing to the
 * readout a few hundred pixels away; "Not started" therefore means planned AND
 * not missed, and every post lands in exactly one axis.
 */
export const MONTH_AXES = [...CONTENT_STAGES, "missed"] as const;
export type MonthAxis = (typeof MONTH_AXES)[number];

export const AXIS_LABEL: Record<MonthAxis, string> = {
  ...STAGE_LABEL,
  missed: "Missed",
};

export function matchesAxis(read: SlotRead, axis: MonthAxis): boolean {
  const missed = isMissed(read.slot);
  if (axis === "missed") return missed;
  if (missed) return false;
  return stageOf(read.slot, read.brief) === axis;
}

/* ===========================================================================
   WHAT YOU MAY DO TO A POST
   ---------------------------------------------------------------------------
   Mirrors PlanSlotUseCases exactly. The server is the gate; these exist so the
   grid does not offer a gesture that is going to come back a 409, and so a card
   can say WHY when it refuses.

   The load-bearing fact, and the one the earlier design got wrong: content and
   time are SEPARATE permissions. A signed-off post's creative is final, but the
   day it runs on is a production decision that stays the team's — "a move
   survives sign-off, which every other slot edit does not". Five of the seven
   states allow a drag.
   =========================================================================== */

/** The states an auto-ideation sweep will pick up, and the only ones a move can
 *  stamp MISSED. The client-review states are deliberately out: the sweep must
 *  never mark a post missed out from under a client's open decision. */
const PRE_SIGNOFF = ["PLANNED", "GENERATING_IDEAS", "IDEATION"];

/** Creative is frozen from sign-off onward — unless the post is DIRECT, which
 *  has no ideas, no brief and no client, so nothing to freeze. */
const CONTENT_FINAL = ["SIGNED_OFF", "IN_CLIENT_REVIEW", "CLIENT_APPROVED"];

/** The ONE state a move refuses: the client is deciding on exactly this post on
 *  exactly this day right now. Recall it and the day is yours again. */
export function canMove(slot: CalendarPlanSlot): boolean {
  return slot.status !== "IN_CLIENT_REVIEW";
}

export function canEditContent(slot: CalendarPlanSlot): boolean {
  return slot.origin === "DIRECT" || !CONTENT_FINAL.includes(slot.status);
}

/** `MANUAL_GENERATE_FROM`. Sign-off has a chosen idea a re-pitch would discard,
 *  the client is mid-decision, and an approved post has a brief built on the
 *  very idea this would delete. */
export function canRepitch(slot: CalendarPlanSlot): boolean {
  return ["PLANNED", "IDEATION", "MISSED"].includes(slot.status);
}

/** Why a gesture is refused, in the server's own words. */
export function refusalFor(slot: CalendarPlanSlot): string | null {
  if (slot.status === "IN_CLIENT_REVIEW") {
    return "With the client. It can't move while they're deciding on it.";
  }
  return null;
}

/**
 * What a post's status BECOMES when it lands on a day — the server rewrites it,
 * so the grid has to say so before the drop rather than surprise you after it.
 *
 * `dayStart` and `todayStart` are both UTC midnight: v2 is day-granular and has
 * no time of day.
 */
export function statusAfterMove(status: string, dayStart: number, todayStart: number): string {
  if (dayStart < todayStart) {
    return PRE_SIGNOFF.includes(status) ? "MISSED" : status;
  }
  return status === "MISSED" ? "PLANNED" : status;
}

/** The one-line consequence of a drop, or null when nothing changes but the day. */
export function moveConsequence(slot: CalendarPlanSlot, dayStart: number, todayStart: number) {
  const next = statusAfterMove(slot.status, dayStart, todayStart);
  if (next === slot.status) return null;
  if (next === "MISSED") return "Lands in the past — it will be marked missed.";
  // Coming back from MISSED restores the auto-ideation budget with it; a post
  // revived without that sits in a state no worker will ever pick up.
  return "Back in the future — it goes back in the queue for ideas.";
}

/* ===========================================================================
   SCOPE
   =========================================================================== */

/** The mix this month was CLAIMED for, snapshotted onto its own plan row. */
export interface MonthTargets {
  static: number;
  vertical: number;
  horizontal: number;
}

export interface PlacedLine {
  label: string;
  placed: number;
  target: number;
}

/**
 * Placed against target, per format bucket.
 *
 * Three lines and never one total: the server holds no fourth number — the sum
 * IS the total — and each bucket is an exact target the planner is held to
 * rather than a ratio. It is also the only readout that can show the case that
 * actually hurts, a month that is FULL but two videos short.
 *
 * MISSED posts are excluded. A post whose day passed before sign-off did not
 * fill the slot it was sold against.
 */
export function placedMix(reads: SlotRead[], targets: MonthTargets | null): PlacedLine[] | null {
  if (!targets) return null;
  const live = reads.filter((r) => r.slot.status !== "MISSED").map((r) => r.slot);
  const statics = live.filter((s) => s.mediaType === "STATIC").length;
  const horizontal = live.filter(
    (s) => s.mediaType !== "STATIC" && s.orientation === "HORIZONTAL",
  ).length;
  return [
    { label: "static", placed: statics, target: targets.static },
    { label: "video · 9:16 / 4:5", placed: live.length - statics - horizontal, target: targets.vertical },
    { label: "video · 16:9", placed: horizontal, target: targets.horizontal },
  ];
}

/**
 * A drop, applied the way `moveToDay` applies it.
 *
 * Lives here rather than in a harness because it is the SERVER'S rule set, and
 * two copies of it would drift: the day is rewritten, the status is rewritten
 * by `statusAfterMove`, a revived MISSED post gets its auto-ideation budget
 * back, and the target day's whole run is renumbered so `order` stays a dense
 * sequence. Optimistic UI in the live app has to do exactly this too, or the
 * card lands in the right cell wearing the wrong state until the refetch.
 */
export function applyMove(
  slots: CalendarPlanSlot[],
  year: number,
  month: number,
  move: { slotId: string; day: number; position: number },
  now: number = Date.now(),
): CalendarPlanSlot[] {
  const d = new Date(now);
  const todayStart = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  const dayStart = Date.UTC(year, month - 1, move.day);

  const moving = slots.find((s) => s.id === move.slotId);
  if (!moving) return slots;

  const status = statusAfterMove(moving.status, dayStart, todayStart) as typeof moving.status;
  const next: CalendarPlanSlot = {
    ...moving,
    scheduledAt: new Date(dayStart).toISOString(),
    status,
    ideaAttempts:
      moving.status === "MISSED" && status !== "MISSED" ? 0 : moving.ideaAttempts,
  };

  const dayOf = (iso: string) => new Date(iso).getUTCDate();
  const mates = slots
    .filter((s) => s.id !== move.slotId && dayOf(s.scheduledAt) === move.day)
    .sort((a, b) => a.order - b.order);
  const at = Math.max(0, Math.min(move.position, mates.length));
  const renumbered = [...mates.slice(0, at), next, ...mates.slice(at)].map((s, i) => ({
    ...s,
    order: i,
  }));

  const byId = new Map(renumbered.map((s) => [s.id, s]));
  return slots.map((s) => byId.get(s.id) ?? s);
}


/**
 * A new post, born the way `AddPlanSlotUseCase` makes one: PLANNED, PIPELINE,
 * appended to the end of its day (`order = last + 1`), with no pillar and no
 * channels — channels live on the accepted idea for a pipeline post, and there
 * is no idea yet.
 */
export function addPost(
  slots: CalendarPlanSlot[],
  planId: string,
  year: number,
  month: number,
  post: {
    day: number;
    title: string;
    mediaType: string;
    aspectRatio: string;
    orientation: string;
    videoLength: string | null;
    pillarId?: string | null;
    pillarName?: string;
  },
): CalendarPlanSlot[] {
  const onDay = slots.filter((s) => new Date(s.scheduledAt).getUTCDate() === post.day);
  const now = new Date().toISOString();
  /* Built FIELD BY FIELD, never by spreading a sibling.
     This used to start from `slots[0]` to borrow the row's shape, and carried
     that slot's ideas and deliverable across with it — so a post added to an
     empty day arrived already "Posted", wearing another post's channels. A new
     post has no ideas, no brief, no media and no client history, and the only
     honest way to say that is to write every field out. */
  const born: CalendarPlanSlot = {
    id: `new-${Math.random().toString(36).slice(2, 9)}`,
    planId,
    title: post.title,
    mediaType: post.mediaType as CalendarPlanSlot["mediaType"],
    aspectRatio: post.aspectRatio as CalendarPlanSlot["aspectRatio"],
    orientation: post.orientation as CalendarPlanSlot["orientation"],
    videoLength: post.videoLength as CalendarPlanSlot["videoLength"],
    pillarId: post.pillarId ?? null,
    pillarName: post.pillarName ?? "",
    scheduledAt: new Date(Date.UTC(year, month - 1, post.day)).toISOString(),
    order: onDay.length,
    status: "PLANNED",
    approvalSource: null,
    approvedByUserId: null,
    origin: "PIPELINE",
    /* [] for a pipeline post, always: channels live on the ACCEPTED idea, and
       there is no idea yet. */
    channels: [],
    ideaAttempts: 0,
    ideaError: null,
    ideas: [],
    reviewDecisions: [],
    deliverable: null,
    createdAt: now,
    updatedAt: now,
  };

  return [...slots, born];
}
